import { Spinner, useDisclosure, useToast } from '@chakra-ui/react'
import type React from 'react'
import InfoBox from '../../../../../../components/InfoBox'
import Toast from '../../../../../../components/Toast'
import Tooltip from '../../../../../../components/Tooltip'
import { useDeleteFeeStructureMutation } from '../../../../../../redux/services/coreApi'
import type {
  CurrencyDto,
  FeeStructureDto,
} from '../../../../../../types/coreApi-types'
import CreateFeeStructureModal from './CreateFeeStructureModal'
import TestQuotation from './TestQuotation'

interface FeeStructureDetailsProps {
  feeStructure: FeeStructureDto | null
  isLoading: boolean
  onUpdated?: (feeStructureId: string) => void
  onDeleted?: (deletedId: string) => void
}

const formatCurrencyList = (currencies: CurrencyDto[]) => {
  if (!currencies || currencies.length === 0) {
    return 'All currencies'
  }
  return currencies.map(currency => currency.isoCode).join(', ')
}

const formatAmount = (value: number) => {
  if (!Number.isFinite(value)) return value
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

const FeeStructureDetails: React.FC<FeeStructureDetailsProps> = ({
  feeStructure,
  isLoading,
  onUpdated,
  onDeleted,
}) => {
  const toast = useToast()
  const testQuotationModal = useDisclosure()
  const updateModal = useDisclosure()
  const [deleteFeeStructure, { isLoading: isDeleting }] =
    useDeleteFeeStructureMutation()

  const handleDelete = async () => {
    if (!feeStructure?.id) return

    const confirmed = window.confirm(
      `Are you sure you want to delete "${feeStructure.description}"? This action cannot be undone.`
    )

    if (confirmed) {
      const deletedId = feeStructure.id
      // Clear selection immediately to prevent refetch of deleted item
      if (onDeleted) {
        onDeleted(deletedId)
      }
      try {
        await deleteFeeStructure(deletedId).unwrap()
        toast({
          position: 'top-right',
          render: () => (
            <Toast type="success">Fee structure deleted successfully.</Toast>
          ),
        })
      } catch (error) {
        toast({
          position: 'top-right',
          render: () => (
            <Toast type="error">
              Failed to delete fee structure. Please try again.
            </Toast>
          ),
        })
      }
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-brand-light-card-border min-h-[300px] flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!feeStructure) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-dashed border-brand-light-card-border min-h-[300px] flex items-center justify-center">
        <p className="text-description text-center px-6">
          Select a fee structure to display its configuration.
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-brand-light-card-border p-6 space-y-8 text-brand-light-text-default">
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
        <div>
          <p className="text-description mb-1 text-sm">Description</p>
          <h2 className="text-2xl font-semibold text-brand-light-text-default">
            {feeStructure.description}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <InfoBox
            colorScheme={feeStructure.thirtyDayRolling ? 'green' : 'gray'}
          >
            {feeStructure.thirtyDayRolling
              ? '30-day rolling window'
              : 'Standard billing'}
          </InfoBox>
          <div className="flex gap-2">
            <Tooltip label="Edit fee structure" placement="top">
              <button
                onClick={updateModal.onOpen}
                className="h-9 w-9 flex items-center justify-center rounded-lg border border-brand-light-card-border hover:bg-brand-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
                disabled={isDeleting}
                aria-label="Edit fee structure"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width={18}
                  height={18}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-brand-light-text-default"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </Tooltip>
            <Tooltip label="Delete fee structure" placement="top">
              <button
                onClick={handleDelete}
                className="h-9 w-9 flex items-center justify-center rounded-lg border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
                disabled={isDeleting}
                aria-label="Delete fee structure"
              >
                {isDeleting ? (
                  <Spinner size="sm" color="red.500" />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width={18}
                    height={18}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-red-600"
                  >
                    <path d="M3 6h18" />
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                )}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">Base fee collections</h3>
        {feeStructure.baseFeeCollections.length === 0 ? (
          <p className="text-description text-sm">
            No base fee collections configured.
          </p>
        ) : (
          <div className="space-y-4">
            {feeStructure.baseFeeCollections.map((collection, index) => (
              <div
                key={collection.id}
                className="border border-brand-light-card-border rounded-2xl p-4 space-y-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-semibold">Collection {index + 1}</p>
                  <div className="flex gap-2 flex-wrap">
                    <InfoBox
                      colorScheme={
                        collection.payConstrained ? 'orange' : 'gray'
                      }
                    >
                      {collection.payConstrained
                        ? `${collection.applicablePayCurrencies.length} pay currencies`
                        : 'All pay currencies'}
                    </InfoBox>
                    <InfoBox
                      colorScheme={
                        collection.receiveConstrained ? 'orange' : 'gray'
                      }
                    >
                      {collection.receiveConstrained
                        ? `${collection.applicableReceiveCurrencies.length} receive currencies`
                        : 'All receive currencies'}
                    </InfoBox>
                    <InfoBox
                      colorScheme={collection.isStepped ? 'green' : 'gray'}
                    >
                      {collection.isStepped ? 'Stepped' : 'Linear'}
                    </InfoBox>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-description mb-1">
                      Applicable pay currencies
                    </p>
                    <p className="font-medium">
                      {collection.payConstrained
                        ? formatCurrencyList(collection.applicablePayCurrencies)
                        : 'Not constrained'}
                    </p>
                  </div>
                  <div>
                    <p className="text-description mb-1">
                      Applicable receive currencies
                    </p>
                    <p className="font-medium">
                      {collection.receiveConstrained
                        ? formatCurrencyList(
                            collection.applicableReceiveCurrencies
                          )
                        : 'Not constrained'}
                    </p>
                  </div>
                  <div>
                    <p className="text-description mb-1">Fee interpolation</p>
                    <p className="font-medium">
                      {collection.isStepped
                        ? 'Stepped (direct transition between brackets)'
                        : 'Linear (interpolated between brackets)'}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-description mb-2 text-sm">Base fees</p>
                  <div className="space-y-2">
                    {collection.baseFees.map(baseFee => (
                      <div
                        key={baseFee.id}
                        className="bg-brand-gray-50 rounded-xl px-4 py-3 flex items-center justify-between text-sm font-semibold text-brand-light-text-default"
                      >
                        <span>
                          ≥ €{formatAmount(baseFee.eurAmountRequired)}
                        </span>
                        <span>{baseFee.feePercent}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">Bank fees</h3>
        {feeStructure.bankFees.length === 0 ? (
          <p className="text-description text-sm">
            No bank fees configured for this structure.
          </p>
        ) : (
          <div className="space-y-3">
            {feeStructure.bankFees.map(bankFee => (
              <div
                key={bankFee.id}
                className="border border-brand-light-card-border rounded-2xl p-4"
              >
                <div className="flex items-center justify-between font-semibold text-sm">
                  <span>Fee percentage</span>
                  <span>{bankFee.feePercent}%</span>
                </div>
                <p className="text-description text-sm mt-2">
                  Currencies:{' '}
                  {bankFee.currencies && bankFee.currencies.length > 0
                    ? formatCurrencyList(bankFee.currencies)
                    : 'Applies to all linked currencies'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-4 border-t border-brand-light-card-border">
        <button
          onClick={testQuotationModal.onOpen}
          className="button"
          type="button"
        >
          Test Quotation
        </button>
      </div>

      {feeStructure && (
        <>
          <TestQuotation
            feeStructure={feeStructure}
            isOpen={testQuotationModal.isOpen}
            onClose={testQuotationModal.onClose}
          />
          <CreateFeeStructureModal
            isOpen={updateModal.isOpen}
            onClose={updateModal.onClose}
            feeStructure={feeStructure}
            onUpdated={id => {
              updateModal.onClose()
              if (onUpdated) {
                onUpdated(id)
              }
            }}
          />
        </>
      )}
    </div>
  )
}

export default FeeStructureDetails
